import { useFormikContext } from "formik";
import { useContext, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { Dialog } from "@mui/material";
import _ from "lodash";
import {
  Box,
  HStack,
  VStack,
  ButtonPrimary,
  ButtonPrimary1,
  isEsopViewer,
  ButtonSecondary,
  SettingHeader,
  SettingsRow,
} from "../../components/utils";
import { useCompanyStore, usePermissionStore } from "../../store";
import {
  useEmployees,
  useEsopPlans,
  useGetAllCompanyConfigs,
  useGetConfigDetails,
  useGrants,
  useSaveConfigCredentials,
  useSaveNewExportConfig,
  useTestHrmsConfig,
  useUpdateExportSettings,
  useUpdateSyncSettings,
  useVestingTemplates,
} from "../../queries";
import { formatDisplayDate } from "../../utils/date";
import {
  ConfigDetails,
  MasterApiData,
  MasterSettings,
  NewConfigSettings,
} from "../../types/ExportExcelEmployee";
import { MultiSelect } from "../../components/shared/MultiSelect";
import { SwitchButton } from "../../components/shared/SwitchButton";
import { Input, Label, TextArea } from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import { useValuationDetails } from "../../queries/Valuation";
import BCHeader from "../../shared/BCHeader";
import { MultiSelectV2 } from "../../components/shared/MultiSelectV2";
import ConfigForm from "./ConfigForm";
import Tooltip from "../../components/shared/Tooltip";
import {
  EmployeeStateOptions,
  StatusOptions,
  SyncSettings,
} from "../../types/common";

export enum EmployeeHrmsStatus {
  PENDING = "pending",
  ACTIVE = "active",
  RESIGNED = "resigned",
  INACTIVE = "inactive",
  EMPLOYED = "employed",
}

export enum EmployeeStates {
  NEW_EMPLOYEES = "new employees",
  EXISTING = "existing",
  ALL = "all",
}

function EmployeeDatasourceSettings() {
  const { permission } = usePermissionStore();
  const { data: planData, isFetched: planDataFetched } = useEsopPlans();
  const { data: grantsData, isFetched: grantsDataFetched } = useGrants();
  const { data: configs } = useGetAllCompanyConfigs();
  const [configId, setConfigId] = useState<string>("");
  const [isDataSaving, setIsDataSaving] = useState<boolean>(false);
  const {
    data: configDetails,
    isFetching,
    refetch: refetchDetails,
  } = useGetConfigDetails(configId);

  const statusOptions = StatusOptions;

  const employeeStateOptions = EmployeeStateOptions;

  const { data: _valuations } = useValuationDetails();
  const { data: allPlans } = useEsopPlans();
  const { data: vestingSchedules } = useVestingTemplates();
  const { mutate: updateSettings } = useUpdateSyncSettings();
  const { mutate: editMasterConfig } = useUpdateExportSettings();
  const [activeTab, setActiveTab] = useState<string>("Settings");
  const [configSettings, setConfigSettings] = useState<SyncSettings>();
  const [isSyncEnabled, setIsSyncEnabled] = useState<boolean>(false);
  const valuations = useMemo(() => _valuations || [], [_valuations]);
  const dataSets = useMemo(() => {
    const dataSources: any[] = [];
    const existingIds: string[] = [];
    if (configs) {
      configs.forEach((config) => {
        const lastSync = config?.employeeDataSyncHistories[0];
        const dataSource = {
          ...config,
          lastSyncedOn: lastSync?.createdAt
            ? formatDisplayDate(new Date(lastSync?.createdAt))
            : "Never",
          lastSyncStatus: lastSync?.syncStatus || "Not Synced",
          lastSyncNote: lastSync?.note || "",
        };
        if (!existingIds.includes(config.id)) {
          dataSources.push(dataSource);
          existingIds.push(config.id);
        }
      });
    }
    return dataSources;
  }, [configs]);
  const saveSettings = () => {
    setIsDataSaving(true);
    if (configDetails?.masterDataType === "GRANT") {
      const updateBody: NewConfigSettings = {
        planFilter: {
          planId: {
            include: [...planFilter],
            exclude: [],
          },
        },
        grantFilter: {
          grantType: {
            include: [grantTypeFilter],
            exclude: [],
          },
        },
        employeeFilter: {
          employMentType: {
            include: [employeeTypeFilter],
            exclude: [],
          },
        },
        enabled: isSyncEnabled,
        name: configName,
        configId,
      };
      editMasterConfig(updateBody, {
        onSuccess: () => {
          toast("Settings updated successfully", {
            type: "success",
            autoClose: 3000,
          });
          refetchDetails();
          setIsDataSaving(false);
        },
        onError: () => {
          toast("Error updating settings", {
            type: "error",
            autoClose: 3000,
          });
          setIsDataSaving(false);
        },
      });
    } else {
      const updateBody: SyncSettings & {
        isSyncEnabled: boolean;
        configId: string;
      } = {
        name: configSettings?.name || "",
        syncFrequency: Number(configSettings?.syncFrequency) || 0,
        additionalSettings: configSettings?.additionalSettings || {},
        planForGrant: configSettings?.planForGrant || "",
        vestingScheduleForGrant: configSettings?.vestingScheduleForGrant || "",
        strikePriceForGrant: configSettings?.strikePriceForGrant || "",
        dataToSync: configSettings?.dataToSync || [],
        considerResignationDateForLapsing:
          configSettings?.considerResignationDateForLapsing || false,
        autoTransferToPersonalEmail:
          configSettings?.autoTransferToPersonalEmail || false,
        valuationForGrant: configSettings?.valuationForGrant || "",
        grantType: configSettings?.grantType,
        grantIdentifierTemplate: configSettings?.grantIdentifierTemplate,
        isSyncEnabled,
        configId,
        syncMode: configSettings?.syncMode || "",
        requireApprovalForResignation:
          configSettings?.requireApprovalForResignation || false,
        autoRaiseRequestForInternalTransfer:
          configSettings?.autoRaiseRequestForInternalTransfer || false,
        internalTransferMatchFields:
          configSettings?.internalTransferMatchFields || [],
      };

      if (
        !updateBody.requireApprovalForResignation &&
        updateBody.autoRaiseRequestForInternalTransfer
      ) {
        toast(
          "Auto raise request for internal transfer cannot be enabled without resignation approval",
          {
            type: "error",
            autoClose: 5000,
          }
        );
        setIsDataSaving(false);
        return;
      }

      updateSettings(updateBody, {
        onSuccess: () => {
          toast("Settings updated successfully", {
            type: "success",
            autoClose: 3000,
          });
          refetchDetails();
          setIsDataSaving(false);
        },
        onError: () => {
          toast("Error updating settings", {
            type: "error",
            autoClose: 3000,
          });
          setIsDataSaving(false);
        },
      });
    }
  };
  const availablePlans = useMemo(
    () =>
      allPlans?.map((plan) => ({
        id: plan?.esopPlanId,
        name: plan?.planName,
        status: plan?.esopPlanState,
      })),
    [allPlans]
  );
  const availableVestingSchedules = useMemo(
    () =>
      vestingSchedules?.map((vs) => ({
        id: vs.id,
        name: vs.vestingTemplateName,
      })),
    [vestingSchedules]
  );
  const [configName, setConfigName] = useState<string>("");
  const [exportType, setExportType] = useState<MasterApiData>(
    MasterApiData.GRANT
  );
  const [credentialDialogData, setCredentialDialogData] = useState<{
    config: ConfigDetails;
    open: boolean;
  }>();
  const { companyData } = useCompanyStore();
  const [planFilter, setPlanFilter] = useState<string[]>([]);
  const [grantTypeFilter, setGrantTypeFilter] = useState<string>("");
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState<string>("");
  const [dataToSync, setDataToSync] = useState<string[]>([]);
  const [newEmployeeFilters, setNewEmployeeFilters] = useState<string[]>([]);
  const [internalTransferMatchFields, setInternalTransferMatchFields] =
    useState<string[]>([]);

  const newConfigSettings = useMemo(() => {
    const configSettings = configDetails?.settings as MasterSettings;
    if (!configSettings) return {};
    const settings: NewConfigSettings = {
      addingNewConfig: false,
      type: exportType,
      name: configDetails?.name || "",
      enabled: isSyncEnabled,
      grantFilter: configSettings.grantFilter,
      planFilter: configSettings.planFilter,
      employeeFilter: configSettings.employeeFilter,
      credentialData: configDetails?.credentialData,
      configId: configDetails?.id,
    };
    return settings;
  }, [configDetails]);
  const [showAdvanceFilters, setShowAdvanceFilters] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);

  const [newConfig, setNewConfig] = useState<NewConfigSettings>({
    addingNewConfig: false,
  });
  useEffect(() => {
    if (configId) {
      refetchDetails();
    }
  }, [configId]);
  const handleAddNewConfig = () => {
    setNewConfig({ ...newConfig, addingNewConfig: true });
  };
  useEffect(() => {
    if (configDetails && configDetails.settings) {
      setNewEmployeeFilters(
        configDetails.settings?.additionalSettings?.employeeImportFilter?.new
          ?.status || []
      );
      setDataToSync(configDetails.settings?.dataToSync || []);
      setInternalTransferMatchFields(
        configDetails.settings?.internalTransferMatchFields || []
      );
      setShowAdvanceFilters(dataToSync.length > 0);
      configDetails.settings.syncMode = configDetails.syncMode || "AUTOMATIC";
      configDetails.settings.name = configDetails.name || "";
      setConfigSettings(configDetails.settings);
      setIsSyncEnabled(configDetails.isEnabled);
    }
  }, [configDetails, isFetching]);

  const imageMaps = {
    greyTip: "/greytip.png",
    darwinBox:
      "https://equion-dev.s3.us-west-2.amazonaws.com/assets/dboxminilogo.png",
    compup: "/earth.png",
  };
  const tabs = ["History", "Settings"];
  const fetchSourceImage = (masterDataType: string) => {
    if (masterDataType === "DarwinboxDataset") {
      return imageMaps.darwinBox;
    }
    if (masterDataType === "GRANT") {
      return "https://rulezerowebsite.s3.amazonaws.com/hissa_site_logo_new.png";
    }
    return imageMaps.greyTip;
  };
  const fetchDestinationImage = (masterDataType: string) => {
    if (masterDataType === "DarwinboxDataset") {
      return "https://rulezerowebsite.s3.amazonaws.com/hissa_site_logo_new.png";
    }
    if (masterDataType === "GRANT") {
      return imageMaps.compup;
    }
    return "";
  };
  const exampleTemplate = useMemo(() => {
    if (!configSettings?.grantIdentifierTemplate) return "";
    const hrId = "E1_1224";
    const grantLength = Math.max(_.random(), 1);
    try {
      const identifier = _.template(configSettings?.grantIdentifierTemplate)({
        hrId,
        grantLength,
      });
      return identifier;
    } catch (e) {
      return "";
    }
  }, [configSettings?.grantIdentifierTemplate]);

  function handleCredentialDialogClose(): void {
    if (credentialDialogData)
      setCredentialDialogData({ ...credentialDialogData, open: false });
    if (configId) {
      refetchDetails();
    }
  }

  const matchFieldOptions = [
    { label: "Name", value: "name" },
    { label: "Email", value: "email" },
    { label: "HR ID", value: "hrId" },
    { label: "Group Date of Joining", value: "groupDateOfJoining" },
  ];
  const matchFieldLabelToValue = Object.fromEntries(
    matchFieldOptions.map((o) => [o.label, o.value])
  );
  const matchFieldValueToLabel = Object.fromEntries(
    matchFieldOptions.map((o) => [o.value, o.label])
  );

  function handleInternalTransferFieldSelection(selectedLabels: string[]) {
    const values = selectedLabels.map((l) => matchFieldLabelToValue[l] ?? l);
    setInternalTransferMatchFields(values);
    setConfigSettings((prevSettings) => {
      if (!prevSettings) return prevSettings;
      return {
        ...prevSettings,
        internalTransferMatchFields: values as any,
      };
    });
  }

  return (
    <Box className="w-full  p-4 bg-white rounded-lg ">
      <VStack className="justify-between p-4 gap-9">
        <Box className="items-center mt-6 text-lg font-medium text-gray-dark">
          <SettingHeader
            title="Employee Datasource Settings"
            description="Configure your employee datasource settings"
          />
        </Box>
        <VStack className="gap-8 max-h-[600px] overflow-y-auto">
          {!configId && (
            <>
              <HStack className="justify-end">
                <ButtonPrimary
                  onClick={() => {
                    handleAddNewConfig();
                  }}
                >
                  Add Export Config
                </ButtonPrimary>
              </HStack>
              <HStack className="justify-between w-full">
                <table className="w-full space-x-2 table-space">
                  <thead className="text-xs font-medium text-[#B5B5C3] ">
                    <tr className="border-b border-[#E4E6EF] border-dashed items-center text-center">
                      <td className="w-1/4">Name & Type</td>
                      <td>Last Synced On</td>
                      <td>Mode</td>
                      <td>Last Sync Status</td>
                      <td>Enabled</td>
                      <td className="w-1/4">Description</td>
                    </tr>
                  </thead>
                  <tbody className="text-xs font-medium text-dark">
                    {dataSets.map((dataSet) => (
                      <tr
                        key={dataSet.id}
                        className="border-b border-[#E4E6EF] border-dashed cursor-pointer hover:bg-slate-50 h-16"
                        onClick={() => setConfigId(dataSet.id)}
                      >
                        <td className="h-16 p-4 w-96 items-center">
                          <VStack className=" items-center text-center gap-4">
                            <HStack>
                              <span className="font-medium">
                                {dataSet.name}
                              </span>
                            </HStack>
                            <HStack className="items-center gap-3">
                              <img
                                src={fetchSourceImage(dataSet.masterDataType)}
                                alt="logo"
                                className="w-8 h-8"
                              />
                              <Icon icon="line-md:arrow-right" width={20} />
                              <img
                                src={fetchDestinationImage(
                                  dataSet.masterDataType
                                )}
                                alt="logo"
                                className="max-w-8 max-h-8"
                              />
                            </HStack>
                          </VStack>
                        </td>
                        <td className="p-4 text-center">
                          {dataSet.lastSyncedOn}
                        </td>
                        <td className="p-4 text-center">{dataSet.syncMode}</td>
                        <td className="p-4 text-center">
                          {dataSet.lastSyncStatus}
                        </td>
                        <td className="p-4 text-center">
                          <input
                            type="checkbox"
                            className="text-white cursor-not-allowed accent-orange-501"
                            checked={dataSet.isEnabled}
                            value={dataSet.isEnabled}
                          />
                        </td>
                        <td className="p-4 text-center">
                          {dataSet.lastSyncNote}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </HStack>
            </>
          )}
          {configId && configDetails && (
            <>
              <HStack className="justify-between w-full">
                <BCHeader
                  onClick={() => {
                    setConfigId("");
                    setConfigSettings(undefined);
                  }}
                  bcTitle="All Configs"
                  bcSubTitle={configDetails.masterDataType}
                />
                <Box className="inline-flex justify-center gap-4 text-lg font-medium">
                  <Box
                    className={`bottom-5 flex items-center justify-center w-auto px-2 h-6 rounded-full ${
                      isSyncEnabled ? "bg-green-300" : "bg-red-300"
                    }`}
                  >
                    <Icon
                      icon={isSyncEnabled ? "mdi:check" : "mdi:close"}
                      className="mr-1 text-white"
                    />
                    <span className="text-xs text-white">
                      {isSyncEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </Box>
                  <SwitchButton
                    className={`inline-flex ${
                      isSyncEnabled ? "cursor-default " : ""
                    } items-center`}
                    value={isSyncEnabled}
                    label={""}
                    onClick={() => {
                      setIsSyncEnabled(!isSyncEnabled);
                    }}
                  />
                </Box>
              </HStack>
              {activeTab === "Settings" && (
                <VStack className="gap-4 mt-4 max-h-[500px] overflow-y-auto p-4">
                  {(configDetails.masterDataType === "DarwinboxDataset" ||
                    configDetails.masterDataType === "greytip") && (
                    <Box className="rounded-lg ">
                      <Box className="flex items-center justify-end w-full mb-4">
                        <p className="mr-2 text-sm font-medium form-label inline-flex gap-2">
                          <ButtonPrimary
                            className="cursor-pointer text-blue-500"
                            onClick={() => {
                              setCredentialDialogData({
                                config: configDetails,
                                open: true,
                              });
                            }}
                          >
                            Manage credentials
                          </ButtonPrimary>
                        </p>
                        {credentialDialogData?.open && (
                          <CredentialDialog
                            open={credentialDialogData.open}
                            onClose={() => handleCredentialDialogClose()}
                            config={credentialDialogData.config}
                          />
                        )}
                      </Box>
                      <HStack className="justify-between w-full gap-4 p-2">
                        <VStack>
                          <p className="mt-4 mr-2 text-sm font-medium form-label">
                            Name of the Integration
                          </p>
                          <p className="text-xs text-gray-500">
                            A unique name to identify this datasource
                            integration.
                          </p>
                        </VStack>
                        <VStack>
                          <Input
                            type="text"
                            value={configSettings?.name || ""}
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              setConfigSettings((prevSettings) => {
                                if (!prevSettings) return prevSettings;
                                return {
                                  ...prevSettings,
                                  name: e.target.value,
                                };
                              });
                            }}
                            className="p-2 border rounded lg:w-48 md:w-32"
                          />
                        </VStack>
                      </HStack>
                      <HStack className="justify-between gap-4 p-2 w-full">
                        <VStack>
                          <p className="mt-4 mr-2 text-sm font-medium form-label">
                            {" "}
                            Sync Frequency (in Days):
                          </p>
                          <p className="text-xs text-gray-500">
                            How often should the system sync employee data from
                            your HRMS to Hissa?
                          </p>
                        </VStack>
                        <VStack>
                          <Input
                            type="number"
                            value={configSettings?.syncFrequency}
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              if (value < 1) {
                                toast(
                                  "Sync Frequency should be greater than 0",
                                  {
                                    type: "error",
                                    autoClose: 3000,
                                  }
                                );
                                return;
                              }
                              setConfigSettings((prevSettings) => {
                                if (!prevSettings) return prevSettings;
                                return {
                                  ...prevSettings,
                                  syncFrequency: Number(e.target.value),
                                };
                              });
                            }}
                            className="p-2 border rounded lg:w-48 md:w-32"
                          />
                        </VStack>
                      </HStack>
                      <HStack className="gap-4 p-2 w-full justify-between">
                        <VStack>
                          <p className="mt-4 mr-2 text-sm font-medium form-label">
                            {" "}
                            Sync Mode:{" "}
                          </p>
                          <p className="text-xs text-gray-500">
                            Choose how the employee data sync should be
                            triggered. Automatic or Manual.
                          </p>
                        </VStack>
                        <VStack>
                          <Select
                            className="lg:w-48 md:w-32"
                            placeholder="Select Sync Mode"
                            options={["AUTOMATIC", "MANUAL"]}
                            textGetter={(mode) => mode}
                            valueGetter={(mode) => mode}
                            value={configSettings?.syncMode || "AUTOMATIC"}
                            onChange={(e) =>
                              setConfigSettings((prevSettings) => {
                                if (!prevSettings) return prevSettings;
                                return {
                                  ...prevSettings,
                                  syncMode: e.target.value as
                                    | "AUTOMATIC"
                                    | "MANUAL",
                                };
                              })
                            }
                          />
                        </VStack>
                      </HStack>
                      <HStack className="justify-between gap-4 p-2 w-full">
                        <VStack>
                          <p className="mt-4 mr-2 text-sm font-medium form-label">
                            Auto Transfer to Personal Email:
                          </p>
                          <p className="text-xs text-gray-500">
                            Transfer employee access to personal email if
                            available.
                          </p>
                        </VStack>
                        <VStack>
                          <Select
                            value={
                              configSettings?.autoTransferToPersonalEmail
                                ? "Yes"
                                : "No"
                            }
                            options={["Yes", "No"]}
                            onChange={(e) =>
                              setConfigSettings((prevSettings) => {
                                if (!prevSettings) return prevSettings;
                                return {
                                  ...prevSettings,
                                  autoTransferToPersonalEmail:
                                    e.target.value === "Yes",
                                };
                              })
                            }
                            className="p-2 border rounded lg:w-48 md:w-32"
                          />
                        </VStack>
                      </HStack>
                      <HStack className="p-2 gap-4 w-full justify-between">
                        <VStack>
                          <p className="mt-4 mr-2 text-sm font-medium form-label">
                            {" "}
                            ESOP plan for Grant:
                          </p>
                          <p className="text-xs text-gray-500">
                            Select the ESOP plan to be associated with grants
                            during employee data sync.
                          </p>
                        </VStack>
                        <VStack>
                          <Select
                            value={configSettings?.planForGrant || ""}
                            options={availablePlans?.map((plan) => plan) || []}
                            textGetter={(plan) =>
                              `${plan.name} (${plan.status})`
                            }
                            valueGetter={(plan) => plan.id}
                            onChange={(e) =>
                              setConfigSettings((prevSettings) => {
                                if (!prevSettings) return prevSettings;
                                return {
                                  ...prevSettings,
                                  planForGrant: e.target.value,
                                };
                              })
                            }
                            className="p-2 border rounded lg:w-48 md:w-32"
                          />
                        </VStack>
                      </HStack>
                      <HStack className="p-2 gap-4 justify-between w-full">
                        <VStack>
                          <p className="mt-4 mr-2 text-sm font-medium form-label">
                            {" "}
                            Vesting Schedule for Grant:
                          </p>
                          <p className="text-xs text-gray-500">
                            Select the vesting schedule to be applied to grants
                            during employee data sync.
                          </p>
                        </VStack>
                        <VStack>
                          <Select
                            value={configSettings?.vestingScheduleForGrant}
                            options={availableVestingSchedules || []}
                            textGetter={(vs) => vs.name}
                            valueGetter={(vs) => vs.id}
                            onChange={(e) =>
                              setConfigSettings((prevSettings) => {
                                if (!prevSettings) return prevSettings;
                                return {
                                  ...prevSettings,
                                  vestingScheduleForGrant: e.target.value,
                                };
                              })
                            }
                            className="border rounded lg:w-48 md:w-32 overflow-ellipsis p-4 pr-6"
                          />
                        </VStack>
                      </HStack>
                      <HStack className="p-2 gap-4 justify-between w-full">
                        <VStack>
                          <p className="mt-4 mr-2 text-sm font-medium form-label">
                            {" "}
                            Strike price for Grant:
                          </p>
                          <p className="text-xs text-gray-500">
                            Select the strike price to be used for grant (
                            {companyData?.currency})
                          </p>
                        </VStack>
                        <VStack>
                          <Input
                            placeholder="Enter Strike Price"
                            type="number"
                            value={configSettings?.strikePriceForGrant || ""}
                            onChange={(e) =>
                              setConfigSettings((prevSettings) => {
                                if (!prevSettings) return prevSettings;
                                return {
                                  ...prevSettings,
                                  strikePriceForGrant: e.target.value,
                                };
                              })
                            }
                            className="p-2 border rounded lg:w-48 md:w-32"
                          />
                        </VStack>
                      </HStack>
                      <HStack className="p-2 pb-4 w-full justify-between gap-4">
                        <VStack>
                          <p className="mt-4 mr-2 text-sm font-medium form-label">
                            Valuation for Grant:
                          </p>
                          <p className="text-xs text-gray-500">
                            Select the valuation to be used for grant
                            calculations during employee data sync.
                          </p>
                        </VStack>
                        <VStack>
                          <Select
                            value={configSettings?.valuationForGrant}
                            options={valuations.map((valuation) => valuation)}
                            textGetter={(valuation) =>
                              `${valuation.name} (${valuation.pps})`
                            }
                            valueGetter={(valuation) => valuation.id}
                            onChange={(e) =>
                              setConfigSettings((prevSettings) => {
                                if (!prevSettings) return prevSettings;
                                return {
                                  ...prevSettings,
                                  valuationForGrant: e.target.value,
                                };
                              })
                            }
                            className="border rounded lg:w-48 md:w-32 p-4 pr-6"
                          />
                        </VStack>
                      </HStack>
                      <HStack className="justify-between gap-4 p-2 w-full">
                        <VStack>
                          <p className="mt-4 mr-2 text-sm font-medium form-label">
                            {" "}
                            Grant Type:
                          </p>
                          <p className="text-xs text-gray-500">
                            Specify the type of grant to be assigned during
                            employee data sync.
                          </p>
                        </VStack>
                        <VStack>
                          <Input
                            placeholder="Enter Grant type"
                            type="text"
                            value={configSettings?.grantType}
                            onChange={(e) => {
                              setConfigSettings((prevSettings) => {
                                if (!prevSettings) return prevSettings;
                                return {
                                  ...prevSettings,
                                  grantType: e.target.value,
                                };
                              });
                            }}
                            className="p-2 border rounded lg:w-48 md:w-32"
                          />
                        </VStack>
                      </HStack>
                      <HStack className="justify-between gap-4 p-2">
                        <VStack>
                          <p className="mt-4 mr-2 text-sm font-medium form-label">
                            Grant Identifier Template:
                          </p>
                          <p className="text-xs text-gray-500">
                            Define a template for generating unique Grant
                            Identifiers during employee data sync. Use
                            placeholders like <code>${"{hrId}"}</code> for
                            Employee HR ID and <code>${"{grantLength}"}</code>{" "}
                            for Grant Length.
                          </p>
                        </VStack>
                        <VStack>
                          <Input
                            placeholder="Enter Grant Identifier"
                            type="text"
                            value={configSettings?.grantIdentifierTemplate}
                            onChange={(e) => {
                              setConfigSettings((prevSettings) => {
                                if (!prevSettings) return prevSettings;
                                return {
                                  ...prevSettings,
                                  grantIdentifierTemplate: e.target.value,
                                };
                              });
                            }}
                            className="p-2 border rounded lg:w-48 md:w-32"
                          />
                          {configSettings?.grantIdentifierTemplate && (
                            <HStack className="justify-end px-4">
                              Example:
                              <span className="text-blue-500">
                                {exampleTemplate}
                              </span>
                            </HStack>
                          )}
                        </VStack>
                      </HStack>
                      <HStack className="justify-between gap-4 p-2 w-full">
                        <VStack>
                          <p className="mt-4 mr-2 text-sm font-medium form-label">
                            Admin Approval for Resigned Employees:
                          </p>
                          <p className="text-xs text-gray-500">
                            Require admin approval for actions related to
                            resigned employees.
                          </p>
                        </VStack>
                        <VStack>
                          <Select
                            value={
                              configSettings?.requireApprovalForResignation
                                ? "Yes"
                                : "No"
                            }
                            options={["Yes", "No"]}
                            onChange={(e) =>
                              setConfigSettings((prevSettings) => {
                                if (!prevSettings) return prevSettings;
                                return {
                                  ...prevSettings,
                                  requireApprovalForResignation:
                                    e.target.value === "Yes",
                                };
                              })
                            }
                            className="p-2 border rounded lg:w-48 md:w-32"
                          />
                        </VStack>
                      </HStack>
                      <HStack className="justify-between gap-4 p-2 w-full">
                        <VStack>
                          <p className="mt-4 mr-2 text-sm font-medium form-label">
                            Auto raise request for internal transfer of
                            employees
                          </p>
                          <p className="text-xs text-gray-500">
                            Automatically raise requests for internal transfer
                            of employees based on name and joining date - last
                            day of employee records
                          </p>
                        </VStack>
                        <VStack>
                          <Select
                            value={
                              configSettings?.autoRaiseRequestForInternalTransfer
                                ? "Yes"
                                : "No"
                            }
                            options={["Yes", "No"]}
                            onChange={(e) =>
                              setConfigSettings((prevSettings) => {
                                if (!prevSettings) return prevSettings;
                                return {
                                  ...prevSettings,
                                  autoRaiseRequestForInternalTransfer:
                                    e.target.value === "Yes",
                                };
                              })
                            }
                            className="p-2 border rounded lg:w-48 md:w-32"
                          />
                        </VStack>
                      </HStack>
                      {configSettings?.autoRaiseRequestForInternalTransfer && (
                        <HStack className="justify-between gap-4 p-2 w-full">
                          <VStack>
                            <p className="mt-4 mr-2 text-sm font-medium form-label">
                              Internal Transfer Match Fields:
                            </p>
                            <p className="text-xs text-gray-500">
                              Select the fields used to match employees for
                              internal transfer detection.
                            </p>
                          </VStack>
                          <MultiSelectV2
                            _className="p-2 border rounded lg:w-48 md:w-32"
                            multiple={true}
                            placeholder={"--Select--"}
                            options={matchFieldOptions.map((o) => ({
                              value: o.label,
                            }))}
                            handleSelection={
                              handleInternalTransferFieldSelection
                            }
                            optionValues={internalTransferMatchFields.map(
                              (v) => matchFieldValueToLabel[v] ?? v
                            )}
                          />
                        </HStack>
                      )}
                      <HStack className="justify-between gap-4 p-2"></HStack>
                      <HStack className="pt-2 items-center gap-2 before:border-b-2 before:border-dashed before:m-auto after:flex-1 after:border-dashed after:content-[''] after:border-b-2">
                        <ButtonPrimary1
                          onClick={() => {
                            setShowAdvanceFilters(!showAdvanceFilters);
                          }}
                          className="flex flex-row bg-transparent border border-solid cursor-pointer border-orange-501 hover:bg-transparent"
                        >
                          Advance Filters
                          {showAdvanceFilters ? (
                            <Icon
                              icon="material-symbols:keyboard-arrow-down-rounded "
                              className=""
                            />
                          ) : (
                            <Icon
                              icon="material-symbols:keyboard-arrow-down-rounded "
                              className=""
                            />
                          )}
                        </ButtonPrimary1>
                      </HStack>

                      {showAdvanceFilters && (
                        <>
                          <HStack className="p-2">
                            <Box className="flex items-center justify-between w-full">
                              <p className="mt-4 mr-2 text-sm font-medium form-label">
                                Data to Sync:
                              </p>
                              {dataToSync && (
                                <MultiSelectV2
                                  _className="p-2 border rounded lg:w-72 md:w-48"
                                  multiple={true}
                                  placeholder={"--Select--"}
                                  options={employeeStateOptions}
                                  handleSelection={(e) => {
                                    setConfigSettings((prevSettings) => {
                                      if (!prevSettings) return prevSettings;
                                      return {
                                        ...prevSettings,
                                        dataToSync: e,
                                      };
                                    });
                                    setDataToSync(e);
                                  }}
                                  optionValues={dataToSync}
                                />
                              )}
                            </Box>
                          </HStack>
                          {(dataToSync.includes(EmployeeStates.NEW_EMPLOYEES) ||
                            dataToSync.includes(EmployeeStates.ALL)) && (
                            <HStack className="p-2">
                              <Box className="flex items-center justify-between w-full">
                                <p className="mt-4 mr-2 text-sm font-medium form-label">
                                  States to Consider:
                                </p>
                                <MultiSelectV2
                                  _className="p-2 border rounded lg:w-72 md:w-48"
                                  multiple={true}
                                  placeholder={"--Select--"}
                                  options={statusOptions}
                                  handleSelection={(e) => {
                                    setConfigSettings((prevSettings) => {
                                      if (!prevSettings) return prevSettings;
                                      return {
                                        ...prevSettings,
                                        additionalSettings: {
                                          ...prevSettings.additionalSettings,
                                          employeeImportFilter: {
                                            ...prevSettings.additionalSettings
                                              ?.employeeImportFilter,
                                            new: {
                                              status: e,
                                            },
                                          },
                                        },
                                      };
                                    });
                                  }}
                                  optionValues={newEmployeeFilters}
                                />
                              </Box>
                            </HStack>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                  {configDetails.masterDataType === "GRANT" && (
                    <Box className="rounded-lg ">
                      {planDataFetched && grantsDataFetched && (
                        <ConfigForm
                          initialData={newConfigSettings || {}}
                          onClose={() => setConfigId("")}
                          isNewConfig={false}
                          isSyncEnabled={isSyncEnabled}
                        />
                      )}
                    </Box>
                  )}
                  {configDetails.masterDataType !== "GRANT" && (
                    <HStack className="justify-between gap-4">
                      <ButtonPrimary1 onClick={() => setConfigId("")}>
                        Cancel
                      </ButtonPrimary1>
                      <ButtonPrimary
                        loading={isDataSaving}
                        onClick={saveSettings}
                      >
                        Save
                      </ButtonPrimary>
                    </HStack>
                  )}
                </VStack>
              )}
            </>
          )}
        </VStack>
      </VStack>
      <Dialog open={newConfig.addingNewConfig || false} maxWidth="md">
        <div className="w-[900px] h-full">
          <ConfigForm
            onClose={() => setNewConfig({ addingNewConfig: false })}
            initialData={{ type: "GRANT" }} // Default values for a new config
            isNewConfig={true}
            isSyncEnabled={isSyncEnabled}
          />
        </div>
      </Dialog>
    </Box>
  );
}
const CredentialDialog = (props: {
  open: boolean;
  onClose: () => void;
  config: ConfigDetails;
}) => {
  const config = useMemo(() => props.config, [props.config]);
  const { credentialData, id, masterDataType } = useMemo(
    () => config,
    [config]
  );
  const [darwinboxData, setDarwinboxData] = useState<ConfigDetails>(
    {} as ConfigDetails
  );
  const { mutate: saveConfigCredentials } = useSaveConfigCredentials();
  const [credentialsValid, setCredentialsValid] = useState<boolean>(false);
  const [operationInProgress, setOperationInProgress] =
    useState<boolean>(false);
  const [showCurrentPassword, setShowCurrentPassword] =
    useState<boolean>(false);
  const { mutate: testDarwinboxCredentials } = useTestHrmsConfig();
  const hasConfigChanged = useMemo(() => {
    if (!credentialData) return false;
    if (masterDataType === "DarwinboxDataset") {
      return (
        darwinboxData.credentialData?.domain !== credentialData.domain ||
        darwinboxData.credentialData?.username !== credentialData.username ||
        darwinboxData.credentialData?.password !== credentialData.password ||
        darwinboxData.credentialData?.apiKey !== credentialData.apiKey ||
        darwinboxData.credentialData?.datasetKey !== credentialData.datasetKey
      );
    }
    return false;
  }, [darwinboxData, credentialData, masterDataType]);

  useEffect(() => {
    setDarwinboxData(config);
  }, [config]);

  const handleConfigChange = (field: string, value: string) => {
    if (credentialsValid) {
      setCredentialsValid(false);
    }
    setDarwinboxData((prevData) => ({
      ...prevData,
      credentialData: {
        ...prevData.credentialData,
        [field]: value,
      },
    }));
  };

  const testCredentials = () => {
    setOperationInProgress(true);
    const requestBody = {
      credentialData: darwinboxData.credentialData,
      type: masterDataType,
      configId: id,
      forPingTest: true,
      createNewConfig: false,
    };
    testDarwinboxCredentials(requestBody, {
      onSuccess: () => {
        toast("Credentials are valid", {
          type: "success",
          autoClose: 2000,
        });
        setCredentialsValid(true);
        setOperationInProgress(false);
      },
      onError: () => {
        toast("Please check your credentials and try again.", {
          type: "error",
          autoClose: 2000,
        });
        setOperationInProgress(false);
      },
    });
  };
  const saveCredentials = () => {
    setOperationInProgress(true);
    const requestBody = {
      credentials: darwinboxData.credentialData,
      configId: id,
    };
    saveConfigCredentials(requestBody, {
      onSuccess: () => {
        toast(
          "Credentials saved successfully. These changes will be applied on the next sync.",
          {
            type: "success",
            autoClose: 2000,
          }
        );
        setOperationInProgress(false);
        props.onClose();
      },
      onError: () => {
        toast("Error saving credentials. Please try again.", {
          type: "error",
          autoClose: 2000,
        });
        setOperationInProgress(false);
      },
    });
  };
  return (
    <Dialog open={props.open} maxWidth="lg" onClose={props.onClose}>
      <div className="p-4 text-lg font-medium border-b stone-700 w-[900px] mx-auto bg-white rounded-lg">
        <h6 className="flex justify-between">
          View / Modify Credentials
          <span onClick={() => props.onClose()} className="cursor-pointer">
            X
          </span>
        </h6>
        <VStack className="p-4 gap-4">
          <VStack className="justify-between w-full">
            <Box className="justify-start w-full "></Box>
            <HStack className="gap-8 pt-8">
              <VStack className="flex-1">
                <Label className="text-sm font-normal">Host Name</Label>
                <Input
                  autoComplete="false"
                  type="text"
                  required
                  disabled={credentialsValid}
                  name=""
                  value={darwinboxData.credentialData?.domain}
                  placeholder="eg : *.darwinbox.in"
                  onChange={(e) => handleConfigChange("domain", e.target.value)}
                />
              </VStack>
              <VStack className="flex-1">
                <Label className="text-sm font-normal">API Username</Label>
                <Input
                  type="text"
                  name="username"
                  required
                  value={darwinboxData.credentialData?.username}
                  disabled={credentialsValid}
                  onChange={(e) =>
                    handleConfigChange("username", e.target.value)
                  }
                />
              </VStack>
              <VStack className="flex-1">
                <Label className="text-sm font-normal">API Password</Label>
                <div className="flex items-center">
                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    name="api_password"
                    required
                    disabled={credentialsValid}
                    value={darwinboxData.credentialData?.password}
                    onChange={(e) =>
                      handleConfigChange("password", e.target.value)
                    }
                  />
                  <Icon
                    icon={`${showCurrentPassword ? "ph:eye-slash" : "ph:eye"}`}
                    width={24}
                    height={24}
                    className="text-orange-501 ml-2 cursor-pointer"
                    onClick={() => {
                      setShowCurrentPassword(!showCurrentPassword);
                    }}
                  />
                </div>
              </VStack>
            </HStack>
            <HStack className="gap-8 pt-8">
              <VStack className="flex-1">
                <Label className="text-sm font-normal">API Key</Label>
                <TextArea
                  name="api_key"
                  required
                  disabled={credentialsValid}
                  value={darwinboxData.credentialData?.apiKey}
                  onChange={(e) => handleConfigChange("apiKey", e.target.value)}
                />
              </VStack>
              <VStack className="flex-1">
                <Label className="text-sm font-normal">Dataset Key</Label>
                <TextArea
                  name="datasetKey"
                  required
                  disabled={credentialsValid}
                  value={darwinboxData.credentialData?.datasetKey}
                  onChange={(e) =>
                    handleConfigChange("datasetKey", e.target.value)
                  }
                />
              </VStack>
            </HStack>
            <HStack className="mt-8"></HStack>
            <HStack className="justify-end gap-4 p-3">
              <ButtonPrimary1
                type="reset"
                className="text-red-500"
                onClick={() => {
                  props.onClose();
                }}
              >
                Cancel
              </ButtonPrimary1>
              {operationInProgress ? (
                <Icon
                  className="animate-spin text-orange-501"
                  icon="lucide:loader-2"
                  width={36}
                />
              ) : (
                <>
                  {!credentialsValid ? (
                    <ButtonPrimary
                      type="reset"
                      disabled={!hasConfigChanged}
                      className="text-red-500"
                      onClick={() => {
                        testCredentials();
                      }}
                    >
                      Test
                    </ButtonPrimary>
                  ) : (
                    <ButtonPrimary
                      type="reset"
                      className="text-red-500"
                      onClick={() => {
                        saveCredentials();
                      }}
                    >
                      Save
                    </ButtonPrimary>
                  )}
                </>
              )}
            </HStack>
          </VStack>
        </VStack>
      </div>
    </Dialog>
  );
};
export default EmployeeDatasourceSettings;
